/**
 * Das Regal · der geteilte Pool ganzer Skripte.
 *
 * Jeder stellt Skripte ein (Text einfügen) oder landet automatisch drin, sobald er sein eigenes
 * Skript bearbeitet. „Übernehmen" schreibt das Skript in die eigene Fassung, ab dem nächsten Call
 * ist sie aktiv.
 *
 * ⚠️ DIESE DATEI HIESS BIS ZUM HERAUSLÖSEN DER BIBLIOTHEK `ScriptLibraryTab` UND STAND MITTEN IN
 * `pages/SalesCrm.tsx`. Wer nach der Skript-Bibliothek suchte, fand stattdessen
 * `pages/salescrm/ScriptLibrary.tsx` — und das war der NACHBARTAB („Skript", der Block-Editor,
 * heute `BlockEditor.tsx` hier daneben). Zwei Namen über Kreuz, an denen sich mehrere Sitzungen
 * verlaufen haben.
 *
 * DIE BEIDEN HÄNGEN ANEINANDER, und das bleibt so: die Knöpfe auf den Karten hier schreiben genau
 * die persönliche Fassung, die der Block-Editor anzeigt. „Übernehmen" legt sie an, „Auf
 * Team-Stand" verwirft sie, „Umbauen" ändert sie. Wer hier etwas ändert, prüft dort mit.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Blocks, BookOpen, Check, ChevronDown, Download, GitBranch, Loader2, Pencil, Plus, RotateCcw,
  Shield, Star, Trash2, TrendingUp, Users, X,
} from 'lucide-react';
import { useStore } from '../../store';
import { salesCrmApi } from '../../services/salesCrmApi';
import { confirmDialog } from '../ui/dialog';
import { TokenTextarea } from '../TokenTextarea';
import { SCRIPT_TOKENS } from '../../lib/scriptTokens';
import SkriptUmbau from './SkriptUmbau';
import type { LibraryScript, ScriptVariant } from '../../types';

// ---------------------------------------------------------------------------

// Die Blockfolge eines Eintrags, aufgeteilt in die zwei Bereiche: der Ablauf und die Einwände.
// Ein Block ohne Vermerk (Alt-Bestand, importierte Skripte) zählt zum Ablauf — dort stand er
// bisher, und ihn hier stillschweigend zu den Einwänden zu schieben hiesse, etwas über ihn zu
// behaupten, was niemand gesagt hat.
export function librarySplit(entry: LibraryScript): { flow: string[]; objections: string[] } {
  const order = entry.section_order?.length ? entry.section_order : Object.keys(entry.sections_data);
  const flow: string[] = [];
  const objections: string[] = [];
  for (const k of order) {
    if (!entry.sections_data[k]) continue;
    (entry.sections_data[k].kind === 'objection' ? objections : flow).push(k);
  }
  return { flow, objections };
}

// Gegenstück zu script_to_text() im Backend — Blöcke als bearbeitbarer Markdown-Text.
export function keysToText(entry: LibraryScript, keys: string[]): string {
  return keys
    .map((k) => {
      const sec = entry.sections_data[k];
      if (!sec) return '';
      return `## ${sec.title || k}\n\n${(sec.body || '').trim()}`;
    })
    .filter(Boolean)
    .join('\n\n');
}

export function nicheLabel(niche: string): string {
  return niche === '_global' ? 'Allgemein' : niche;
}

export default function Regal() {
  const {
    libraryScripts, libraryLoading, fetchLibraryScripts, addLibraryScript, updateLibraryScript,
    removeLibraryScript, adoptLibraryScript, setLibraryScriptAsMain, salesNiches, user,
  } = useStore();
  const lang = user?.lang || 'de';
  const canSetMain = user?.role === 'admin' || user?.role === 'head_of_sales';

  const [nicheFilter, setNicheFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Das Skript, das gerade umgebaut wird — Absatz für Absatz eine andere Fassung (SkriptUmbau.tsx).
  const [umbau, setUmbau] = useState<LibraryScript | null>(null);
  // Mein eigenes Arbeits-Skript: wie viele Absätze weichen vom Team-Stand ab? Das ist der Gegenpol
  // zur Liste darunter — sie zeigt, was ES GIBT, diese Zeile zeigt, was für MICH gilt.
  const [meinFork, setMeinFork] = useState<ScriptVariant | null>(null);
  const [forkBusy, setForkBusy] = useState(false);

  // Formular — dient Neuanlage UND Bearbeiten (editId gesetzt).
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [fName, setFName] = useState('');
  const [fNiche, setFNiche] = useState('');
  const [fLang, setFLang] = useState(lang);
  const [fNotes, setFNotes] = useState('');
  const [fContent, setFContent] = useState('');
  // Der zweite Bereich. Eigenes Feld und nicht bloss eine Überschriftenregel im ersten: was ein
  // Einwand ist, entscheidet der, der das Skript einstellt — nicht ein Titel, der zufällig mit
  // „Einwand" beginnt.
  const [fObjections, setFObjections] = useState('');
  const [saving, setSaving] = useState(false);
  // Woraus dieses neue Skript abgezweigt wurde. Nur zur Anzeige im Formular — die Herkunft selbst
  // steht in der Notiz, damit sie auch noch dasteht, wenn das Formular längst zu ist.
  const [branchFrom, setBranchFrom] = useState<LibraryScript | null>(null);
  const [branchOpen, setBranchOpen] = useState(false);

  useEffect(() => {
    fetchLibraryScripts(nicheFilter || undefined);
  }, [fetchLibraryScripts, nicheFilter]);

  const ladeMeinSkript = useCallback(() => {
    salesCrmApi
      .getMyFork(nicheFilter || '_global', lang)
      .then((r) => setMeinFork(r.fork))
      .catch(() => setMeinFork(null));
  }, [nicheFilter, lang]);

  useEffect(() => { ladeMeinSkript(); }, [ladeMeinSkript]);

  // Live-Abgleich: jemand anderes hat woanders (Block-Editor, Umbau-Fenster) die Bibliothek oder
  // seinen Fork geändert → hier neu laden. Regal zeigt immer den Telefon-Kanal (`bereich: 'skript'`,
  // siehe fetchLibraryScripts im Store), also denselben Filter wie BlockEditor.tsx anwenden.
  useEffect(() => {
    const onUpdate = (e: Event) => {
      const b = (e as CustomEvent).detail?.bereich;
      if (b && b !== 'skript') return;
      fetchLibraryScripts(nicheFilter || undefined);
      ladeMeinSkript();
    };
    window.addEventListener('sales-script-updated', onUpdate);
    return () => window.removeEventListener('sales-script-updated', onUpdate);
  }, [fetchLibraryScripts, ladeMeinSkript, nicheFilter]);

  // Zurück zum Team-Stand. Das Gegenstück zu „Für mich übernehmen" im Umbau-Fenster und zu
  // „Übernehmen" auf jeder Karte: drei Wege, EIN Skript, und keiner davon eine Einbahnstrasse.
  const zurueckAufTeamStand = async () => {
    if (!(await confirmDialog({
      message: 'Dein Skript wieder auf den Team-Stand setzen? Deine eigenen Absätze gehen dabei verloren — die Fassungen der anderen bleiben in der Bibliothek und sind jederzeit wieder übernehmbar.',
      tone: 'danger',
    }))) return;
    setForkBusy(true);
    try {
      await salesCrmApi.resetMyFork(nicheFilter || '_global', lang);
      window.dispatchEvent(new CustomEvent('sales-script-updated'));
      ladeMeinSkript();
      fetchLibraryScripts(nicheFilter || undefined);
      setMsg({ kind: 'ok', text: 'Dein Skript entspricht wieder dem Team-Stand.' });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setForkBusy(false);
    }
  };

  // Das Formular steht über der Liste. Wer unten auf „Branchen" drückt, soll nicht raten müssen,
  // wo das ausgefüllte Formular hin ist.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (formOpen && branchFrom) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [formOpen, branchFrom]);

  // Beliebtheit heisst hier: wie viele Leute es je übernommen haben. Das ist die einzige Zahl, die
  // wirklich gezählt wird (`script_library_adoptions`) — ein „wie oft geöffnet" gibt es nicht.
  // Gleichstand entscheidet, womit mehr telefoniert wurde, danach das jüngere Skript.
  const byPopularity = [...libraryScripts]
    .filter((e) => e.id !== editId)
    .sort((a, b) => (b.adopted_count - a.adopted_count)
      || (b.calls - a.calls)
      || b.updated_at.localeCompare(a.updated_at));

  const resetForm = () => {
    setEditId(null);
    setFName('');
    setFNiche('');
    setFLang(lang);
    setFNotes('');
    setFContent('');
    setFObjections('');
    setBranchFrom(null);
    setBranchOpen(false);
  };

  const openNew = () => {
    resetForm();
    setFormOpen(true);
  };

  // Abzweigen: ein bestehendes Skript als Grundlage nehmen und daraus ein ZWEITES machen — das
  // Original bleibt unangetastet. Der übliche Fall ist die kleine Abweichung (ein anderer
  // Einstieg, eine Nische mehr), für die bisher nur zwei Wege offenstanden: alles neu tippen oder
  // das Original überschreiben und damit die Fassung verlieren, mit der andere schon telefonieren.
  const openBranch = (entry: LibraryScript) => {
    const { flow, objections } = librarySplit(entry);
    setEditId(null);
    setBranchFrom(entry);
    setBranchOpen(false);
    setFName(`${entry.name} — Abzweig`);
    setFNiche(entry.niche === '_global' ? '' : entry.niche);
    setFLang(entry.language);
    // Die Herkunft in die Notiz, nicht bloss in den Formular-Zustand: sie ist das einzige Feld,
    // das den Abzweig überlebt und später in der Liste erklärt, woher er kommt.
    setFNotes(`Abzweig von „${entry.name}"`);
    setFContent(keysToText(entry, flow));
    setFObjections(keysToText(entry, objections));
    setFormOpen(true);
    setMsg(null);
  };

  const openEdit = (entry: LibraryScript) => {
    setEditId(entry.id);
    setFName(entry.name);
    setFNiche(entry.niche === '_global' ? '' : entry.niche);
    setFLang(entry.language);
    setFNotes(entry.notes ?? '');
    const { flow, objections } = librarySplit(entry);
    setFContent(keysToText(entry, flow));
    setFObjections(keysToText(entry, objections));
    setFormOpen(true);
  };

  const submit = async () => {
    if (!fName.trim() || !fContent.trim()) {
      setMsg({ kind: 'err', text: 'Name und Skript-Text sind Pflicht.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        name: fName.trim(),
        content: fContent,
        // IMMER mitschicken, auch leer: das Feld ist zugleich die Aussage „das sind alle
        // Einwände". Liesse man es bei leerem Text weg, bliebe beim Bearbeiten der alte
        // Einwand-Bestand stehen, den man gerade weggelöscht hat.
        objections: fObjections,
        niche: fNiche.trim().toLowerCase() || '_global',
        language: fLang,
        notes: fNotes.trim() || undefined,
      };
      if (editId) {
        const updated = await updateLibraryScript(editId, payload);
        // Was mit der Änderung passiert ist, statt nur „gespeichert": bis 2026-08-17 blieb sie im
        // Pool liegen und erreichte niemanden — auch den Autor nicht, der weiter mit der alten
        // Fassung telefonierte. Wer jetzt speichert, soll sehen, dass sie angekommen ist.
        const n = updated.resynced ?? 0;
        setMsg({
          kind: 'ok',
          text: n === 0
            ? 'Skript aktualisiert. Es telefoniert gerade niemand damit — wer es übernimmt, bekommt die neue Fassung.'
            : n === 1
              ? 'Skript aktualisiert · 1 Arbeits-Skript hat die neue Fassung, ab dem nächsten Call.'
              : `Skript aktualisiert · ${n} Arbeits-Skripte haben die neue Fassung, ab dem nächsten Call.`,
        });
      } else {
        await addLibraryScript(payload);
        setMsg({
          kind: 'ok',
          text: branchFrom
            ? `Abzweig steht jetzt allen zur Verfügung — „${branchFrom.name}" gibt es unverändert weiter.`
            : 'Skript steht jetzt allen zur Verfügung.',
        });
      }
      await fetchLibraryScripts(nicheFilter || undefined);
      setFormOpen(false);
      resetForm();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const adopt = async (entry: LibraryScript) => {
    setBusyId(entry.id);
    setMsg(null);
    try {
      await adoptLibraryScript(entry.id);
      setMsg({ kind: 'ok', text: `„${entry.name}" ist ab dem nächsten Call dein Skript.` });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  const makeMain = async (entry: LibraryScript) => {
    setBusyId(entry.id);
    setMsg(null);
    try {
      // `released` = wie viele Arbeits-Skripte dem neuen Team-Stand wieder folgen. Steht dabei,
      // weil ein Main-Wechsel bis 2026-08-17 niemanden erreichte, der je etwas übernommen hatte —
      // die Meldung „ist jetzt Main" stimmte, und trotzdem änderte sich im Cockpit nichts.
      const n = await setLibraryScriptAsMain(entry.id);
      setMsg({
        kind: 'ok',
        text: `„${entry.name}" ist jetzt Main für ${nicheLabel(entry.niche)}.`
          + (n ? ` ${n} Arbeits-Skript${n === 1 ? '' : 'e'} folgen wieder dem Team-Stand — eigene Änderungen bleiben.` : ''),
      });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (entry: LibraryScript) => {
    if (!(await confirmDialog({ message: `„${entry.name}" aus der Bibliothek nehmen?`, tone: 'danger' }))) return;
    setBusyId(entry.id);
    try {
      await removeLibraryScript(entry.id);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Kopfzeile */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-text-main flex items-center gap-2">
            <BookOpen size={18} className="text-accent" /> Skript-Bibliothek
          </h2>
          <p className="text-xs text-text-muted mt-0.5">
            Skripte des Teams — einstellen, ansehen, übernehmen, branchen. Wer sein eigenes Skript
            bearbeitet, landet automatisch hier.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={nicheFilter}
            onChange={(e) => setNicheFilter(e.target.value)}
            className="bg-bg-input border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
          >
            <option value="">Alle Nischen</option>
            {salesNiches.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <button
            onClick={openNew}
            className="px-3 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 flex items-center gap-1.5"
          >
            <Plus size={15} /> Skript einstellen
          </button>
        </div>
      </div>

      {msg && (
        <div
          className={`text-xs rounded-lg px-3 py-2 border ${
            msg.kind === 'ok'
              ? 'text-emerald-600 bg-emerald-500/10 border-emerald-500/30'
              : 'text-danger bg-danger/10 border-danger/30'
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Mein Skript — was für MICH gilt, während die Liste darunter zeigt, was es GIBT.
          Ohne diese Zeile ist ein umgebautes eigenes Skript unsichtbar: es steht in keiner Liste,
          weil es niemandem gehört ausser mir, und man merkt es erst im nächsten Call. */}
      <div className="flex items-center gap-2 flex-wrap text-[11px] rounded-lg border border-border-primary bg-bg-elevated/50 px-3 py-2">
        <GitBranch size={12} className="text-accent shrink-0" />
        <span className="text-text-main font-medium">Mein Skript</span>
        {(() => {
          const eigene = meinFork?.overridden_sections?.length ?? 0;
          const uebernommen = libraryScripts.find((e) => e.adopted_by_me);
          if (uebernommen) {
            return (
              <span className="text-text-muted">
                · „{uebernommen.name}" übernommen{eigene ? ` · ${eigene} Absätze davon selbst geändert` : ''}
              </span>
            );
          }
          return (
            <span className="text-text-muted">
              {eigene
                ? `· eigene Fassung mit ${eigene} geänderten Absätzen`
                : '· entspricht dem Team-Stand'}
            </span>
          );
        })()}
        <div className="flex-1" />
        {/* „Version von anderen" ist die Liste unten (jede Karte hat „Übernehmen"), „zurück zum
            Original" ist dieser Knopf — er erscheint nur, wenn es etwas zurückzunehmen gibt. */}
        {((meinFork?.overridden_sections?.length ?? 0) > 0 || libraryScripts.some((e) => e.adopted_by_me)) && (
          <button
            onClick={zurueckAufTeamStand}
            disabled={forkBusy}
            className="px-2 py-1 text-[11px] text-text-muted hover:text-text-main border border-border-primary rounded-md flex items-center gap-1 disabled:opacity-50"
            title="Alle eigenen Änderungen verwerfen und wieder mit dem Team-Stand telefonieren"
          >
            {forkBusy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />} Auf Team-Stand
          </button>
        )}
      </div>

      {/* Formular */}
      {formOpen && (
        <div ref={formRef} className="border border-border-primary rounded-xl bg-bg-elevated p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-text-main">
              {editId ? 'Skript bearbeiten' : branchFrom ? 'Abzweig' : 'Neues Skript'}
            </span>
            <button onClick={() => { setFormOpen(false); resetForm(); }} className="text-text-muted hover:text-text-main">
              <X size={16} />
            </button>
          </div>

          {/* Beim Abzweigen sieht das Formular aus wie das Original — die Zeile sagt, dass das
              Absenden trotzdem ein ZWEITES Skript anlegt und das Original nicht anrührt. Ohne sie
              wäre „Einstellen" von „Speichern" nicht zu unterscheiden. */}
          {!editId && branchFrom && (
            <div className="text-[11px] rounded-lg px-3 py-2 border border-accent/30 bg-accent/5 text-text-muted flex items-start gap-2">
              <GitBranch size={13} className="text-accent mt-0.5 shrink-0" />
              <span>
                Grundlage: <span className="text-text-main font-medium">„{branchFrom.name}"</span> —
                ändere, was anders sein soll. „{branchFrom.name}" bleibt unverändert; hier entsteht
                ein zweites Skript daneben.
              </span>
            </div>
          )}

          {/* Skript branchen — nur bei der Neuanlage. Wer bearbeitet, hat seine Grundlage schon. */}
          {!editId && (
            <div className="border border-border-primary/70 rounded-lg bg-bg-surface/40 overflow-hidden">
              <button
                onClick={() => setBranchOpen(!branchOpen)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-bg-surface/70"
              >
                <span className="text-xs font-medium text-text-main flex items-center gap-1.5">
                  <GitBranch size={13} className="text-accent" />
                  Skript branchen
                  <span className="text-[10px] text-text-muted font-normal">
                    — bestehendes als Grundlage nehmen, meistgenutzte zuerst
                  </span>
                </span>
                <ChevronDown
                  size={14}
                  className={`text-text-muted transition-transform ${branchOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {branchOpen && (
                <div className="border-t border-border-primary/70 max-h-60 overflow-y-auto divide-y divide-border-primary/50">
                  {byPopularity.length === 0 ? (
                    <p className="text-[11px] text-text-muted px-3 py-3">
                      Noch nichts da, wovon sich abzweigen liesse.
                    </p>
                  ) : (
                    byPopularity.map((e) => {
                      const s = librarySplit(e);
                      return (
                        <button
                          key={e.id}
                          onClick={() => openBranch(e)}
                          className="w-full text-left px-3 py-2 hover:bg-accent/5 flex items-center justify-between gap-3"
                        >
                          <span className="min-w-0">
                            <span className="block text-xs text-text-main truncate">{e.name}</span>
                            <span className="block text-[10px] text-text-muted truncate">
                              von {e.author ?? 'unbekannt'} · {nicheLabel(e.niche)} · {e.language.toUpperCase()}
                              {' · '}{s.flow.length} Blöcke
                              {s.objections.length > 0 && ` · ${s.objections.length} Einwände`}
                            </span>
                          </span>
                          <span className="flex items-center gap-2 shrink-0 text-[10px] text-text-muted">
                            <span className="flex items-center gap-1" title="Übernahmen — wie viele Leute damit telefoniert haben">
                              <Users size={10} /> {e.adopted_count}
                            </span>
                            {e.calls > 0 && (
                              <span className="flex items-center gap-1 text-text-main">
                                <TrendingUp size={10} className="text-accent" /> {e.conversion_pct}%
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })
                  )}
                  {nicheFilter && (
                    <p className="text-[10px] text-text-muted px-3 py-2">
                      Zeigt nur „{nicheFilter}" — der Nischen-Filter oben gilt auch hier.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-text-muted">Name</label>
              <input
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                placeholder="z. B. Harter Einstieg v3"
                className="mt-1 w-full bg-bg-input border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted">Nische (leer = für alle)</label>
              <input
                value={fNiche}
                onChange={(e) => setFNiche(e.target.value)}
                list="library-niches"
                placeholder="Allgemein"
                className="mt-1 w-full bg-bg-input border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
              />
              <datalist id="library-niches">
                {salesNiches.map((n) => <option key={n} value={n} />)}
              </datalist>
            </div>
            <div>
              <label className="text-xs text-text-muted">Sprache</label>
              <select
                value={fLang}
                onChange={(e) => setFLang(e.target.value)}
                className="mt-1 w-full bg-bg-input border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          {/* Zwei Bereiche, zwei Felder. Der Ablauf ist die Reihenfolge, in der man spricht; die
              Einwände sind die Antworten, die man NICHT der Reihe nach vorliest, sondern
              aufschlägt, wenn sie kommen. Bis 2026-08-17 war beides ein Feld — im Call landeten
              die Einwandantworten dadurch mitten im Ablauf, es sei denn, die Überschrift begann
              zufällig mit „Einwand". Das Cockpit trennt beides längst (Hauptfluss vs.
              Abzweigung); ihm fehlte nur die Auskunft, was wohin gehört. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-main font-medium flex items-center gap-1.5">
                Skript · Ablauf
                <span className="text-[10px] text-text-muted/70 font-normal">
                  — <code>## Überschrift</code> trennt die Blöcke; ohne Überschrift wird alles ein Block
                </span>
              </label>
              <p className="text-[10px] text-text-muted mt-0.5">
                Wird im Call von oben nach unten durchgegangen. <code>[</code> tippen → Baustein.
              </p>
              <TokenTextarea
                value={fContent}
                onChange={setFContent}
                rows={12}
                placeholder={'## Einstieg\n\nHallo [name], hier ist [callername] von [anbieter] ...\n\n## Magische Frage\n\nWas müsste bei [firmenname] passieren, damit ...'}
                className="mt-1 w-full bg-bg-input border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main font-mono focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="text-xs text-text-main font-medium flex items-center gap-1.5">
                <Shield size={12} className="text-amber-400" /> Einwände
                <span className="text-[10px] text-text-muted/70 font-normal">— optional</span>
              </label>
              <p className="text-[10px] text-text-muted mt-0.5">
                Eine Überschrift je Einwand. Im Call per Taste <code>1</code>–<code>9</code>
                {' '}erreichbar — nicht im Ablauf.
              </p>
              <TokenTextarea
                value={fObjections}
                onChange={setFObjections}
                rows={12}
                placeholder={'## Keine Zeit\n\nVerstehe [name] — deshalb halte ich es kurz ...\n\n## Kein Interesse\n\nDarf ich trotzdem eine Frage stellen ...'}
                className="mt-1 w-full bg-bg-input border border-amber-400/25 rounded-lg px-3 py-2 text-sm text-text-main font-mono focus:outline-none focus:border-amber-400/60"
              />
            </div>
          </div>

          {/* Auch für den Fall, dass ein fertiges Skript hier hineinkopiert wird: dann tippt
              niemand `[`, und ohne diese Zeile bliebe unbekannt, dass es die Bausteine gibt. */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[10px] text-text-muted">
            <span>Füllt sich beim Anruf von selbst:</span>
            {SCRIPT_TOKENS.slice(0, 6).map((t) => (
              <code key={t.key} title={t.hinweis} className="text-accent/90">[{t.key}]</code>
            ))}
            <span>· im Feld <code>[</code> tippen zeigt alle</span>
          </div>

          <div>
            <label className="text-xs text-text-muted">Notiz (optional)</label>
            <input
              value={fNotes}
              onChange={(e) => setFNotes(e.target.value)}
              placeholder="Wofür ist das Skript gut? Wann funktioniert es?"
              className="mt-1 w-full bg-bg-input border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={submit}
              disabled={saving}
              className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {editId ? 'Speichern' : 'Einstellen'}
            </button>
            <button
              onClick={() => { setFormOpen(false); resetForm(); }}
              className="px-3 py-2 text-text-muted hover:text-text-main text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Liste */}
      {libraryLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-muted py-6">
          <Loader2 size={16} className="animate-spin" /> Bibliothek laden…
        </div>
      ) : libraryScripts.length === 0 ? (
        <div className="border border-dashed border-border-primary rounded-xl p-8 text-center">
          <BookOpen size={22} className="text-text-muted mx-auto mb-2" />
          <p className="text-sm text-text-main font-medium">Noch keine Skripte in der Bibliothek</p>
          <p className="text-xs text-text-muted mt-1">
            Stelle eins ein — oder bearbeite dein eigenes Skript vor einem Call, dann landet es automatisch hier.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {libraryScripts.map((entry) => {
            const split = librarySplit(entry);
            const isOwn = entry.created_by === user?.id;
            const busy = busyId === entry.id;
            const open = expandedId === entry.id;
            return (
              <div key={entry.id} className="border border-border-primary rounded-xl bg-bg-elevated overflow-hidden">
                <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-text-main">{entry.name}</span>
                      {entry.adopted_by_me && (
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-accent/10 text-accent border border-accent/30">
                          mein Skript
                        </span>
                      )}
                      {entry.is_auto && (
                        <span
                          className="px-1.5 py-0.5 rounded-md text-[10px] bg-bg-surface text-text-muted border border-border-primary"
                          title="Automatisch aus dem Arbeits-Skript des Autors übernommen"
                        >
                          automatisch
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-text-muted flex-wrap">
                      <span>von {entry.author ?? 'unbekannt'}</span>
                      <span>· {nicheLabel(entry.niche)} · {entry.language.toUpperCase()}</span>
                      {/* Die Zahl der Einwände steht daneben, sobald es welche gibt: „14 Blöcke"
                          sagt nichts darüber, ob dieses Skript für den Moment gerüstet ist, in dem
                          das Gespräch kippt. */}
                      <span>· {split.flow.length} Blöcke</span>
                      {split.objections.length > 0 && (
                        <span className="flex items-center gap-1 text-amber-300/80">
                          · <Shield size={11} /> {split.objections.length} Einwände
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        · <Users size={11} /> {entry.adopted_count} Übernahmen
                      </span>
                      {/* Ohne Calls die Zeile NICHT ausblenden: sonst sieht ein ungemessenes Skript
                          aus wie eines ohne Messung überhaupt. Die Null benennen, nicht verstecken. */}
                      {entry.calls > 0 ? (
                        <span className="flex items-center gap-1 text-text-main">
                          · <TrendingUp size={11} className="text-accent" />
                          {entry.conversion_pct}% aus {entry.calls} Calls
                        </span>
                      ) : (
                        <span
                          className="flex items-center gap-1"
                          title="Die Termin-Quote zählt nur Calls, die jemand im Zeitraum seiner Übernahme geführt hat. Solange niemand dieses Skript übernommen und damit telefoniert hat, gibt es nichts zu messen."
                        >
                          · <TrendingUp size={11} /> noch keine Calls gemessen
                        </span>
                      )}
                    </div>
                    {entry.notes && (
                      <p className="text-[11px] text-text-muted mt-1.5 italic">{entry.notes}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => setExpandedId(open ? null : entry.id)}
                      className="px-2.5 py-1.5 text-xs text-text-muted hover:text-text-main border border-border-primary rounded-lg flex items-center gap-1"
                    >
                      <ChevronDown size={13} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
                      Ansehen
                    </button>
                    <button
                      onClick={() => setUmbau(entry)}
                      className="px-2.5 py-1.5 text-xs text-text-muted hover:text-accent border border-border-primary rounded-lg flex items-center gap-1"
                      title="Absatz für Absatz eine andere Fassung wählen — aus den Skripten der anderen oder selbst geschrieben"
                    >
                      <Blocks size={13} /> Umbauen
                    </button>
                    {/* „Branchen" nimmt das GANZE Skript als Vorlage und lässt einen den Text neu
                        schreiben; „Umbauen" tauscht einzelne Absätze gegen vorhandene Fassungen.
                        Beide führen zu einem neuen Eintrag — der Unterschied ist, wie viel man
                        selbst tippt. */}
                    <button
                      onClick={() => openBranch(entry)}
                      className="px-2.5 py-1.5 text-xs text-text-muted hover:text-accent border border-border-primary rounded-lg flex items-center gap-1"
                      title="Als Grundlage für ein neues Skript nehmen — dieses hier bleibt unverändert"
                    >
                      <GitBranch size={13} /> Branchen
                    </button>
                    <button
                      onClick={() => adopt(entry)}
                      disabled={busy || entry.adopted_by_me}
                      className="px-2.5 py-1.5 bg-accent text-white rounded-lg text-xs font-medium hover:bg-accent/90 disabled:opacity-40 flex items-center gap-1"
                      title="Dieses Skript als mein Skript verwenden"
                    >
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                      {entry.adopted_by_me ? 'Aktiv' : 'Übernehmen'}
                    </button>
                    {canSetMain && (
                      <button
                        onClick={() => makeMain(entry)}
                        disabled={busy}
                        className="px-2.5 py-1.5 text-xs text-text-muted hover:text-accent border border-border-primary rounded-lg flex items-center gap-1 disabled:opacity-40"
                        title="Als Main-Version dieser Nische setzen"
                      >
                        <Star size={13} /> Main
                      </button>
                    )}
                    {(isOwn || canSetMain) && (
                      <>
                        <button
                          onClick={() => openEdit(entry)}
                          className="p-1.5 text-text-muted hover:text-text-main border border-border-primary rounded-lg"
                          title="Bearbeiten"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => remove(entry)}
                          disabled={busy}
                          className="p-1.5 text-text-muted hover:text-danger border border-border-primary rounded-lg disabled:opacity-40"
                          title="Aus der Bibliothek nehmen"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Angesehen wird in denselben zwei Bereichen, in denen eingetragen wurde — und in
                    denen der Call es später zeigt. Drei Ansichten desselben Skripts, die
                    auseinanderlaufen, wären drei Skripte. Ein Eintrag ohne Einwände zeigt die
                    zweite Überschrift gar nicht erst: eine leere Rubrik sieht aus wie ein Fehler. */}
                {open && (
                  <div className="border-t border-border-primary bg-bg-surface/50 p-4 space-y-4 max-h-[50vh] overflow-y-auto">
                    {([
                      { keys: split.flow, label: 'Ablauf', ton: 'text-text-muted' },
                      { keys: split.objections, label: 'Einwände', ton: 'text-amber-300/90' },
                    ] as const).filter((b) => b.keys.length).map((bereich) => (
                      <div key={bereich.label} className="space-y-3">
                        {(split.objections.length > 0) && (
                          <div className={`text-[10px] uppercase tracking-wider ${bereich.ton}`}>
                            {bereich.label} · {bereich.keys.length}
                          </div>
                        )}
                        {bereich.keys.map((key) => {
                          const sec = entry.sections_data[key];
                          if (!sec) return null;
                          return (
                            <div key={key}>
                              <div className="text-xs font-medium text-text-main mb-1">{sec.title || key}</div>
                              <p className="text-[13px] leading-relaxed text-text-muted whitespace-pre-wrap">{sec.body}</p>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {umbau && (
        <SkriptUmbau
          entry={umbau}
          canReplace={umbau.created_by === user?.id || canSetMain}
          onClose={() => setUmbau(null)}
          onSaved={(hinweis) => {
            setMsg({ kind: 'ok', text: hinweis });
            // „Für mich übernehmen" ändert die Zeile „Mein Skript" — sie muss es sofort zeigen,
            // sonst behauptet sie weiter „entspricht dem Team-Stand".
            ladeMeinSkript();
            fetchLibraryScripts(nicheFilter || undefined);
          }}
        />
      )}
    </div>
  );
}
